import * as React from 'react';
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  getTheme,
  ITextStyles,
  Link,
  MessageBar,
  MessageBarType,
  Stack,
  Text
} from '@fluentui/react';
import { getDisplayName } from 'components/util';
import { settingsButtonStyles } from './SettingsStyles';
import { QueueConfiguration, postUpsertQueueConfig } from '../../services';
import { useHistory } from 'react-router';
import { SettingsLeavePromptMessage } from '../navigation';
import { AdminContext } from '../../Store/Admin';
import { SalusPrimaryButton } from '../shared/SalusPrimaryButton';

interface QueueTitleProps {
  saveActive: boolean;
  queueDataState: QueueConfiguration;
}

const displayNamesStyle: ITextStyles = {
  root: {
    height: 'auto',
    lineHeight: 36,
    marginTop: 15
  }
};

interface UpsertPrompt {
  show: boolean;
  title: string;
  subText: string;
  isSuccessfulQueueUpsert: boolean;
}

const defaultClosedUpsertPrompt: UpsertPrompt = {
  show: false,
  title: '',
  subText: '',
  isSuccessfulQueueUpsert: false
};

const successfulUpsertPrompt: UpsertPrompt = {
  show: true,
  title: 'Save successful',
  subText: 'Your changes have been saved. Moderators may need to refresh their app to see the changes.',
  isSuccessfulQueueUpsert: true
};

const unsuccessfulUpsertPrompt: UpsertPrompt = {
  show: true,
  title: 'Save failed',
  subText:
    'Your changes have not been saved. Click Try again or refresh the page, you will have to make your changes again in this case.',
  isSuccessfulQueueUpsert: false
};

export const QueueTitle: React.FunctionComponent<QueueTitleProps> = (props: QueueTitleProps) => {
  const { palette, fonts } = getTheme();

  const [serviceError, setServiceError] = React.useState<boolean>(false);
  const [showPrompt, setShowPrompt] = React.useState<boolean>(false);
  const [upsertPrompt, setUpsertPrompt] = React.useState<UpsertPrompt>(defaultClosedUpsertPrompt);
  const { dispatch } = React.useContext(AdminContext);
  const history = useHistory();

  const goToDashboard = () => {
    dispatch({ type: 'CLEAR_QUEUE_CONFIG' });
    history.push(`/dashboard?tab=${props.queueDataState.tenant.tenantId}`);
  };

  const onReturnToDashboard = () => {
    if (props.saveActive) {
      setShowPrompt(true);
    } else {
      goToDashboard();
    }
  };

  async function onSaveButtonClick(queueData: QueueConfiguration | undefined): Promise<void> {
    // Making GMC call to upsert configuration
    if (queueData !== null) {
      let data: QueueConfiguration;
      try {
        setServiceError(false);
        // TODO: is there a case where this data will be different than queueData?
        // do we need to update the state with it? (and do we have a full state update func)
        data = await postUpsertQueueConfig(queueData as QueueConfiguration);
        dispatch({ type: 'UPDATE_QUEUE_LIST_WITH_MODIFIED_QUEUE', payload: data });
        setUpsertPrompt(successfulUpsertPrompt);
        const active = document.querySelector('.is-selected') as HTMLInputElement;
        if (active) {
          active.focus();
        }
      } catch (exception) {
        setServiceError(true);
        setUpsertPrompt(unsuccessfulUpsertPrompt);
        if (unsuccessfulUpsertPrompt.title == 'Save failed') {
          const msModal = document.querySelector('.ms-Modal');
          const tryfailed = msModal?.querySelector('button');
          if (tryfailed?.title != 'Try again') {
            const active = document.querySelector('.is-selected') as HTMLInputElement;
            if (active) {
              active.focus();
            }
          }
        }
        return;
      }
    }
  }

  return (
    <>
      <Link
        style={{ marginLeft: 55, marginTop: 16, color: palette.themeDarkAlt, width: 'max-content' }}
        onClick={onReturnToDashboard}
      >
        {`< ${props.queueDataState.tenant.displayName} Dashboard `}
      </Link>
      <Stack horizontal horizontalAlign="space-between" tokens={{ childrenGap: 50 }} style={{ marginLeft: 55 }}>
        <Stack>
          <Text as={'h1'} variant={'xxLargePlus'} styles={displayNamesStyle}>
            {props.queueDataState.tenant.displayName}{' '}
            {getDisplayName(props.queueDataState.subjectType, props.queueDataState.displayName)}{' '}
            {props.queueDataState.title.displayName}
          </Text>
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 97 }} styles={{ root: { marginRight: 94, marginTop: 12 } }}>
          <div className="ml-4">
            <SalusPrimaryButton
              className="ml-5"
              text="Save"
              disabled={!props.saveActive}
              onClick={(): Promise<void> => onSaveButtonClick(props.queueDataState)}
              styles={settingsButtonStyles(palette, fonts, 74)}
            />
          </div>
        </Stack>
      </Stack>
      {serviceError && (
        <Stack style={{ marginLeft: '-1rem' }}>
          <Stack.Item>
            <MessageBar
              messageBarType={MessageBarType.error}
              isMultiline={false}
              dismissButtonAriaLabel="Close"
              styles={{ iconContainer: { marginLeft: 73 } }}
            >
              There was an error while attempting to save the configuration, please try again.
            </MessageBar>
          </Stack.Item>
        </Stack>
      )}
      <Dialog
        hidden={!showPrompt}
        dialogContentProps={{
          title: 'Exit to Dashboard?',
          subText: SettingsLeavePromptMessage,
          titleProps: {
            'aria-level': 2
          }
        }}
        modalProps={{
          overlay: {
            isDarkThemed: true
          }
        }}
      >
        <DialogFooter>
          <SalusPrimaryButton onClick={goToDashboard} text="Yes" />
          <DefaultButton onClick={() => setShowPrompt(false)} text="No" />
        </DialogFooter>
      </Dialog>
      <Dialog
        isBlocking={false}
        hidden={!upsertPrompt.show}
        dialogContentProps={{
          title: upsertPrompt.title,
          subText: upsertPrompt.subText,
          titleProps: {
            'aria-level': 2
          }
        }}
        modalProps={{
          overlay: {
            isDarkThemed: true
          }
        }}
      >
        <DialogFooter>
          {!upsertPrompt.isSuccessfulQueueUpsert && (
            <SalusPrimaryButton
              onClick={(): Promise<void> => onSaveButtonClick(props.queueDataState)}
              text="Try again"
            />
          )}
          <DefaultButton
            onClick={() => setUpsertPrompt({ ...upsertPrompt, show: false })}
            text={upsertPrompt.isSuccessfulQueueUpsert ? 'Done' : 'Cancel'}
          />
        </DialogFooter>
      </Dialog>
    </>
  );
};
